import { For, JSX, Show } from 'solid-js';
import { t } from '@trimble/i18n';
import {
  CoordinateOrder,
  CoordinateOrderOptions,
  CoordinateSettings,
  FormattingSettings as ProjectFormattingSettings,
  FormattingSettingsOptions,
} from '@trimble/types';
import {
  classNames,
  IFormattingSettings,
  IUnitSystem,
  UnitSystems,
  UnitSystemType,
} from '@fulcrum/lib';
import { getFormattingSystemDefaults } from '@trimble/utilities';
import { UnitSystemsTranslationMap } from './formatting-settings-helper';

export interface UnitDisplaySettingsSelectorProps {
  settings: ProjectFormattingSettings;
  coordinateSettings?: CoordinateSettings;
  onCoordinateSettingsChanged(order: CoordinateOrder): void;
  onSettingsChanged(values: Partial<IFormattingSettings>): void;
  selectedCoordinateOrder: CoordinateOrder | undefined;
  options: FormattingSettingsOptions;
}

export const UnitDisplaySettingsSelector = (
  props: UnitDisplaySettingsSelectorProps
): JSX.Element => {
  const coordinateOrderLabel = 'formatting-settings-coordinate-order-label';
  const unitSystemLabel = 'formatting-settings-unit-system-label';

  const unitSystemType = (): UnitSystemType => {
    return props.settings.unitSystem.toLowerCase() as UnitSystemType;
  };

  const filterCustomSystem = (value: [string, IUnitSystem]): boolean =>
    !(
      (value[0] as UnitSystemType) === 'custom' &&
      !props.options.enableCustomSystem &&
      unitSystemType() !== 'custom'
    );

  const UnitSystemSelector = (): JSX.Element => (
    <div class="input-group">
      <label id={unitSystemLabel}>
        {t('shared.ui.unitSettings.unitSystem')}
      </label>
      <div class="mt-[0.2rem]">
        <select
          data-testid="unit-system-selector"
          class={classNames(
            'settings-select',
            'trimble-formatting-singleColumn'
          )}
          aria-labelledby={unitSystemLabel}
          disabled={!props.options.enableUnitSystemSelection}
          onChange={(e) => {
            props.onSettingsChanged(
              getFormattingSystemDefaults(
                e.currentTarget.value as UnitSystemType
              )
            );
          }}
        >
          <For
            each={Object.entries(UnitSystems).filter((value) =>
              filterCustomSystem(value)
            )}
          >
            {([key]) => (
              <option
                class="select-option"
                value={key}
                selected={unitSystemType() === key}
              >
                {t(UnitSystemsTranslationMap.get(key as UnitSystemType) ?? '')}
              </option>
            )}
          </For>
        </select>
      </div>
      <Show when={props.options.enableDescriptions}>
        <span class={classNames('modus-wc-text-xs')}>
          {t('shared.ui.unitSettings.unitSystemDescription')}
        </span>
      </Show>
    </div>
  );

  const CoordinateOrderSelector = (): JSX.Element =>
    props.coordinateSettings && (
      <div class="input-group">
        <label id={coordinateOrderLabel}>
          {t('shared.ui.coordinateSettings.coordinateOrder')}
        </label>
        <div class="mt-[0.2rem]">
          <select
            aria-labelledby={coordinateOrderLabel}
            disabled={!props.options.enableUnitSystemSelection}
            onChange={(e) =>
              props.onCoordinateSettingsChanged(
                e.currentTarget.value as CoordinateOrder
              )
            }
          >
            <For each={Object.entries(CoordinateOrderOptions)}>
              {([key, value]) => (
                <option
                  class="select-option"
                  value={key}
                  selected={props.selectedCoordinateOrder === key}
                >
                  {t(value)}
                </option>
              )}
            </For>
          </select>
        </div>
      </div>
    );
  return (
    <>
      <UnitSystemSelector />
      <CoordinateOrderSelector />
    </>
  );
};
